import AbstractModule from './AbstractModule';
import {$document, APP_NAME} from '../utils/environment';
import {EVENT as PAYMENT_POPUP_EVENT} from './PaymentPopup';
import {Event as FORM_EVENT} from './Form';
import {EVENT as CART_FORM_EVENT} from './CartForm';

const MODULE_NAME = 'PaymentForm';
const EVENT_NAMESPACE = `${APP_NAME}.${MODULE_NAME}`;

export const EVENT = {
    CHARGE_BOOKING:      `chargeBooking.${EVENT_NAMESPACE}`,
    PAYMENT_INTENT_DONE: `paymentIntentDone.${EVENT_NAMESPACE}`,
    SUBMIT:              `submit.${EVENT_NAMESPACE}`,
    CHANGE:              `change.${EVENT_NAMESPACE}`,
};

const SELECTOR = {
    card:   '.js-card-element',
    form:   '.js-form',
    amount: '.js-amount',

    bookingInput:       'input[name=bookingId]',
    amountInput:        'input[name=amount]',
    paymentMethodInput: 'input[name=paymentMethodId]',
    paymentIntentInput: 'input[name=paymentIntentId]',
    ERROR_MSG:          '.js-global-error-msg',
};

export default class extends AbstractModule {
    /**
     * Constructor.
     *
     * @return {void}
     */
    constructor(options) {
        super(options);

        // ELEMENTS
        this.$card = this.$el.find(SELECTOR.card);
        this.$form = this.$el.find(SELECTOR.form);
        this.$amount = this.$el.find(SELECTOR.amount);

        // INPUTS
        this.$paymentMethodInput = this.$el.find(SELECTOR.paymentMethodInput);
        this.$paymentIntentInput = this.$el.find(SELECTOR.paymentIntentInput);
        this.$amountInput = this.$el.find(SELECTOR.amountInput);
        this.$bookingInput = this.$el.find(SELECTOR.bookingInput);
        this.$globalErrorMsg = this.$el.find(SELECTOR.ERROR_MSG);

        // REFS
        this.cardElement = null;
        this.cardParsed = false;

        // Regular vars
        this.apiKey = options.key;

        this.$form.on(FORM_EVENT.SUBMIT, (event) => this.onSubmit(event));
    }

    init() {
        // End it here for now.
        if (typeof Stripe === 'undefined') {
            this.loadStripeScript();

            return this;
        }
        this.initStripe();
        this.cardElement.on(EVENT.CHANGE, () => this.cardParsed = false);
        $document.on(EVENT.CHARGE_BOOKING, (event) => this.onChargeBooking(event));
        $document.on(EVENT.PAYMENT_INTENT_DONE, (options) => this.onPaymentIntent(options));
    }

    onPaymentIntent(options) {
        const {response} = options;

        if (response.confirmation) {
            const {confirmation} = response;

            // Require some kind of double validation
            if (confirmation.status === 'requires_action' &&
                confirmation.next_action.type === 'use_stripe_sdk'
            ) {
                this.stripeInstance.handleCardAction(
                    confirmation.client_secret
                ).then((result) => {
                    if (result.error) {
                        this.$globalErrorMsg.html(result.error.message).slideDown();
                    } else {
                        console.log('Requiring extra validation');
                        this.$paymentIntentInput.val(result.paymentIntent.id);
                        this.$form.submit();
                    }
                })
            } else {
                // The payment was successful
                $document.triggerHandler({
                    type: CART_FORM_EVENT.PAYMENT_DONE,
                    response: response
                })
            }
        } else {
            // Should never end up here
            console.warn('Something went wrong!');
        }
    }

    onSubmit(event) {
        event.stopPropagation();
        event.preventDefault();

        if (!this.cardParsed) {
            if (!this.$paymentIntentInput.val()) {
                this.$form.data('prevent-submission', true);

                this.stripeInstance.createPaymentMethod(
                    'card',
                    this.cardElement
                ).then((result) => {
                    if (result.error) {
                        // handle errors and re-enable the form
                        this.$globalErrorMsg.html(result.error.message).slideDown();
                        return;
                    } else {
                        const token = result.paymentMethod.id;
                        this.$paymentMethodInput.val(token);

                        this.cardParsed = true;
                        this.$form.data('prevent-submission', false);

                        return this.$form.submit();
                    }
                });
            }

            // this.stripeInstance.createToken(this.cardElement)
            //     .then((result) => {
            //         if (result.error) {
            //             // handle errors and re-enable the form
            //             return
            //         } else {
            //             const token = result.token;
            //             this.$tokenInput.val(token.id)
            //
            //             this.cardParsed = true
            //             this.$form.data('prevent-submission', false)
            //
            //             return this.$form.submit()
            //         }
            //     })
        }

        this.cardParsed = false;
    }

    onChargeBooking(event) {
        let {
            options: {
                amount,
                bookingId,
            }
        } = event;

        this.cardParsed = false;
        this.$amount.html('£' + parseFloat(amount / 100));
        this.$amountInput.val(amount);
        this.$bookingInput.val(bookingId);
        this.$paymentMethodInput.val(null);
        this.$paymentIntentInput.val(null);

        $document.triggerHandler({
            type: PAYMENT_POPUP_EVENT.TOGGLE,
            flag: true
        });
    }

    /**
     * Destructor.
     *
     * @return {void}
     */
    destroy() {
        super.destroy();
        this.$el.off(`.${EVENT_NAMESPACE}`);
        $document.off(`.${EVENT_NAMESPACE}`);
    }

    // ==========================================================================
    // STRIPE MODULE
    // ==========================================================================

    initStripe() {
        if (this.stripeInstance) {
            return this;
        }

        this.stripeInstance = Stripe(this.apiKey);

        var elements = this.stripeInstance.elements();
        this.cardElement = elements.create('card', {
            style: {
                base: {
                    color: '#32325D',
                    fontFamily: 'proxima-nova, "Helvetica Neue", Helvetica, sans-serif',
                    fontSmoothing: 'antialiased',
                    fontSize: '16px',
                    '::placeholder': {
                        color: '#AAB7C4'
                    }
                },
                invalid: {
                    color: '#FA755A',
                    iconColor: '#FA755A'
                }
            }
        });

        this.cardElement.mount(this.$card.get(0));
    }

    /**
     * Loads the script from stripe.com
     * @return {thisArg} Chainable.
     */
    loadStripeScript() {
        $.getScript('https://js.stripe.com/v3/', (response) => {
            this.init();
        });

        return this;
    }
}
